import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

const REBUY_ADD_WINDOW_MS = 5000;
const REBUY_SYNC_DELAY_MS = 150;
const CART_ADD_PATH_PATTERN = /\/cart\/add(\.js)?$/;

let lastRebuyInteractionAt = 0;
let pendingSyncTimeout = 0;
let syncPromise = null;
let rebuyDrawerObserver = null;

function getDrawerRebuyWidgetIds() {
  return [...document.querySelectorAll('.cart-rebuy-product [data-rebuy-id]')]
    .map((element) => element.getAttribute('data-rebuy-id'))
    .filter(Boolean);
}

function refreshDrawerRebuyWidgets() {
  const rebuyWidgets = window.Rebuy?.widgets;
  if (!Array.isArray(rebuyWidgets) || rebuyWidgets.length === 0) return;

  const widgetIds = getDrawerRebuyWidgetIds();
  if (widgetIds.length === 0) return;

  rebuyWidgets
    .filter((widget) => widgetIds.includes(String(widget?.id)))
    .forEach((widget) => {
      widget.getWidgetProducts?.();
    });
}

function moveDrawerRebuyPrices() {
  const productBlocks = document.querySelectorAll('.cart-rebuy-product .rebuy-product-block');

  productBlocks.forEach((productBlock) => {
    const productInfo = productBlock.querySelector('.rebuy-product-info');
    const productActions = productBlock.querySelector('.rebuy-product-actions');

    if (!productInfo || !productActions) return;

    const productPrice = productInfo.querySelector('.rebuy-product-price');
    if (!productPrice || productActions.contains(productPrice)) return;

    productActions.prepend(productPrice);
  });
}

function startDrawerObserver() {
  if (rebuyDrawerObserver) return;

  rebuyDrawerObserver = new MutationObserver(() => {
    moveDrawerRebuyPrices();
  });

  rebuyDrawerObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

function markRebuyInteraction(event) {
  if (!(event.target instanceof Element)) return;

  const rebuyButton = event.target.closest('.rebuy-widget .rebuy-button');
  if (!rebuyButton) return;

  lastRebuyInteractionAt = Date.now();
}

function isRecentRebuyInteraction() {
  return Date.now() - lastRebuyInteractionAt <= REBUY_ADD_WINDOW_MS;
}

function isCartAddRequest(url, method) {
  return method.toUpperCase() === 'POST' && CART_ADD_PATH_PATTERN.test(url.pathname);
}

function getRequestUrl(input) {
  if (typeof input === 'string') {
    return new URL(input, window.location.origin);
  }

  if (input instanceof URL) {
    return new URL(input.toString(), window.location.origin);
  }

  return new URL(input.url, window.location.origin);
}

function getRequestMethod(input, init) {
  if (init?.method) return init.method;
  if (input instanceof Request) return input.method;
  return 'GET';
}

function getCartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((component) => component.dataset.sectionId)
    .filter(Boolean)
    .filter((sectionId, index, list) => list.indexOf(sectionId) === index);
}

function scheduleCartSync() {
  window.clearTimeout(pendingSyncTimeout);
  pendingSyncTimeout = window.setTimeout(() => {
    syncRebuyCart().catch((error) => {
      console.error('Failed to sync Rebuy cart state:', error);
    });
  }, REBUY_SYNC_DELAY_MS);
}

async function syncRebuyCart() {
  if (syncPromise) return syncPromise;

  syncPromise = (async () => {
    lastRebuyInteractionAt = 0;

    const sectionIds = getCartSectionIds();
    const [cart, sections] = await Promise.all([
      fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json()),
      Promise.all(
        sectionIds.map(async (sectionId) => {
          const html = await sectionRenderer.getSectionHTML(sectionId, false);
          return [sectionId, html];
        })
      ).then((entries) => Object.fromEntries(entries)),
    ]);

    document.dispatchEvent(
      new CartAddEvent(cart, 'rebuy-cart-sync', {
        source: 'rebuy-cart-sync',
        itemCount: cart.item_count,
        sections,
      })
    );

    requestAnimationFrame(() => {
      refreshDrawerRebuyWidgets();
      moveDrawerRebuyPrices();
    });
  })();

  try {
    await syncPromise;
  } finally {
    syncPromise = null;
  }
}

function patchFetch() {
  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const response = await nativeFetch(input, init);

    try {
      const url = getRequestUrl(input);
      const method = getRequestMethod(input, init);

      if (response.ok && isRecentRebuyInteraction() && isCartAddRequest(url, method)) {
        scheduleCartSync();
      }
    } catch (error) {
      console.error('Failed to inspect Rebuy fetch request:', error);
    }

    return response;
  };
}

function patchXHR() {
  const nativeOpen = XMLHttpRequest.prototype.open;
  const nativeSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function open(method, url, ...rest) {
    this.__rebuyCartSync = {
      method,
      url: new URL(url, window.location.origin),
    };

    return nativeOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function send(body) {
    this.addEventListener(
      'loadend',
      () => {
        const request = this.__rebuyCartSync;
        if (!request) return;

        if (this.status >= 200 && this.status < 300 && isRecentRebuyInteraction() && isCartAddRequest(request.url, request.method)) {
          scheduleCartSync();
        }
      },
      { once: true }
    );

    return nativeSend.call(this, body);
  };
}

document.addEventListener('click', markRebuyInteraction, true);
moveDrawerRebuyPrices();
startDrawerObserver();
patchFetch();
patchXHR();
